import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

const AVATAR_COLLECTION = [
  { id: "pink-w",   img: "/assets/home/avatar-pink-w.png",   color: "#FF2E9A" },
  { id: "purple-w", img: "/assets/home/avatar-purple-w.png", color: "#9B6BFF" },
  { id: "orange-w", img: "/assets/home/avatar-orange-w.png", color: "#FFB547" },
  { id: "red-m",    img: "/assets/home/avatar-red-m.png",    color: "#FF3D5A" },
  { id: "blue-m",   img: "/assets/home/avatar-blue-m.png",   color: "#4FC3FF" },
  { id: "green-m",  img: "/assets/home/avatar-green-m.png",  color: "#3DD68C" },
];

function FloatingParticles({ density = 12, color = "#9B6BFF" }) {
  const particles = Array.from({ length: density }, (_, i) => ({
    left: ((i * 11 + 5) % 100),
    size: 1.5 + (i % 4) * 0.7,
    delay: (i * 0.6) % 8,
    duration: 9 + (i % 5),
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {particles.map((p, i) => (
        <span
          key={i}
          style={{
            position: "absolute", top: 0,
            left: `${p.left}%`,
            width: p.size, height: p.size,
            borderRadius: "50%",
            background: color, boxShadow: `0 0 ${p.size * 3}px ${color}`,
            opacity: 0.45,
            animation: `vcFloatUp ${p.duration}s linear infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes vcFloatUp {
          0% { transform: translateY(110vh); opacity: 0; }
          15% { opacity: 0.7; }
          85% { opacity: 0.7; }
          100% { transform: translateY(-12vh); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/**
 * Step 2 of the Remotely flow — host names the room + picks their avatar.
 * Continue → pack picker.
 */
export default function PlayRemotelyCreateRoom({ onBack, onContinue, defaultName }) {
  const [roomName, setRoomName] = useState("");
  const [hostName, setHostName] = useState(defaultName || "");
  const [avatarId, setAvatarId] = useState(
    localStorage.getItem("remote_avatar_id") || "pink-w"
  );

  const handleContinue = () => {
    if (!roomName.trim()) {
      toast.error("Give your room a name");
      return;
    }
    if (!hostName.trim()) {
      toast.error("Add your name");
      return;
    }
    localStorage.setItem("remote_avatar_id", avatarId);
    onContinue?.({ roomName: roomName.trim(), hostName: hostName.trim(), avatarId });
  };

  const selectedAv = AVATAR_COLLECTION.find((a) => a.id === avatarId);

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="remotely-createroom-screen">
      <FloatingParticles density={14} color="#9B6BFF" />
      <main className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-3 relative z-[2]">
          <button
            onClick={onBack}
            aria-label="Back"
            data-testid="remotely-createroom-back"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
            style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10" aria-hidden />
        </div>

        {/* Title */}
        <div className="text-center mb-7 mt-2">
          <motion.div
            animate={{ scale: [1, 1.18, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="inline-block text-[20px] mb-1"
            style={{ filter: "drop-shadow(0 0 10px #9B6BFF)" }}
          >
            💜
          </motion.div>
          <h1
            className="font-extrabold leading-[0.95] tracking-tight block"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(2.2rem, 9.5vw, 2.7rem)",
              letterSpacing: "-0.035em",
              background: "linear-gradient(90deg, #E0D6FF 0%, #9B6BFF 55%, #6A4DFF 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              textShadow: "0 0 1px rgba(155, 107, 255, 0.6)",
              filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
            }}
            data-testid="remotely-createroom-title"
          >
            Create your room
          </h1>
          <p className="text-white/65 mt-2.5 font-medium" style={{ fontSize: "14px" }}>
            Name it. Make it yours.
          </p>
        </div>

        {/* Room name */}
        <div className="mb-5">
          <label className="block text-[11px] font-bold text-white/65 uppercase tracking-[0.18em] mb-1.5 pl-1">
            Room name
          </label>
          <input
            value={roomName}
            onChange={(e) => setRoomName(e.target.value.slice(0, 24))}
            placeholder="Date Night Vibes"
            data-testid="remotely-createroom-roomname"
            className="w-full bg-transparent outline-none text-white text-[15px] font-medium placeholder-white/35 py-3 px-4 rounded-2xl"
            style={{
              background: "rgba(20, 14, 26, 0.6)",
              border: "1px solid rgba(155, 107, 255, 0.55)",
              boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 0 14px rgba(155, 107, 255, 0.25)",
              backdropFilter: "blur(8px)",
            }}
          />
        </div>

        {/* Your name */}
        <div className="mb-7">
          <label className="block text-[11px] font-bold text-white/65 uppercase tracking-[0.18em] mb-1.5 pl-1">
            Your name
          </label>
          <input
            value={hostName}
            onChange={(e) => setHostName(e.target.value.slice(0, 20))}
            placeholder="Jamie"
            data-testid="remotely-createroom-hostname"
            className="w-full bg-transparent outline-none text-white text-[15px] font-medium placeholder-white/35 py-3 px-4 rounded-2xl"
            style={{
              background: "rgba(20, 14, 26, 0.6)",
              border: "1px solid rgba(155, 107, 255, 0.45)",
              boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 0 14px rgba(155, 107, 255, 0.18)",
              backdropFilter: "blur(8px)",
            }}
          />
        </div>

        {/* Avatar picker */}
        <div className="mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="flex-1 h-px max-w-[60px]" style={{ background: "linear-gradient(90deg, transparent, rgba(155, 107, 255, 0.55))" }} />
            <h3 className="text-white text-[14px] font-extrabold tracking-tight inline-flex items-center gap-1.5" style={{ letterSpacing: "-0.01em" }}>
              Choose your vibe
              <motion.span
                animate={{ scale: [1, 1.18, 1], rotate: [0, 8, -8, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                style={{ filter: "drop-shadow(0 0 6px #9B6BFF)" }}
              >
                ✨
              </motion.span>
            </h3>
            <div className="flex-1 h-px max-w-[60px]" style={{ background: "linear-gradient(90deg, rgba(155, 107, 255, 0.55), transparent)" }} />
          </div>
          <div className="snap-carousel flex gap-3 -mx-5 px-5 pb-2 justify-center" data-testid="remotely-createroom-avatar-list">
            {AVATAR_COLLECTION.map((av) => {
              const sel = avatarId === av.id;
              return (
                <button
                  key={av.id}
                  onClick={() => setAvatarId(av.id)}
                  data-testid={`remotely-createroom-avatar-${av.id}`}
                  className="flex-shrink-0 relative transition active:scale-95"
                  style={{ width: 64, height: 64 }}
                >
                  {sel && (
                    <motion.div
                      animate={{ scale: [1, 1.18, 1], opacity: [0.6, 0.95, 0.6] }}
                      transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                      className="absolute inset-[-8px] rounded-full pointer-events-none"
                      style={{
                        background: `radial-gradient(circle, ${av.color}80 0%, transparent 70%)`,
                        filter: "blur(8px)",
                      }}
                      aria-hidden
                    />
                  )}
                  <div
                    className="relative w-full h-full rounded-full overflow-hidden"
                    style={{
                      border: `2.5px solid ${sel ? av.color : `${av.color}66`}`,
                      boxShadow: sel
                        ? `0 0 22px ${av.color}cc, inset 0 0 12px ${av.color}33`
                        : `0 0 10px ${av.color}66`,
                      background: "#0a060f",
                    }}
                  >
                    <img src={av.img} alt="" className="w-full h-full object-cover" />
                    {sel && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 400 }}
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ background: av.color, boxShadow: `0 0 10px ${av.color}, 0 0 4px #fff` }}
                      >
                        <Check className="w-3 h-3 text-white" strokeWidth={4} />
                      </motion.div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1" />

        {/* Continue CTA */}
        <button
          onClick={handleContinue}
          data-testid="remotely-createroom-continue"
          className="vc-cta vc-cta-primary w-full rounded-full py-4 font-extrabold flex items-center justify-center gap-2 mt-2"
          style={{ fontSize: "16px" }}
        >
          Continue
          <ArrowRight className="w-5 h-5" strokeWidth={3} />
        </button>
      </main>
    </div>
  );
}
